import { WarningIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  Flex,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Stack,
  StackDivider,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'

import { ProfilePicturePopover } from '@components/Admins/ProfilePicturePopover'
import { AppContext, ScreenContext } from '@hb/react'
import { useContext, useMemo, useState } from 'react'
import { AddEncounterPopover } from '../../../../../charting/Encounters/AddEncounterPopover'
import { useProfile } from '../../../../../contexts/user/ProfileContext'
import { useUpdateDoc } from '../../../../../hooks/backend/useUpdateDoc'
import { ProfileRedFlagPopover } from '../../../../DataView/RedFlagPopover'
import { DefaultModal } from '../../../../Modals'
import { PatientMidwifeInfo } from '../../../../Practices'
import { AssessmentSelect } from '../../Assessments/AssessmentSelect'
import { ReportDeliveryPopover } from '../../Assessments/ReportDeliveryPopover'
import { UploadFilePopover } from '../../Files/UploadFilePopover'
import { GynInvitePopover } from '../../Gyn/SendGynInvite'
import { NameEditModal } from '../../Patient/NameEditModal'
import { PartnerPopover } from '../../Patient/PartnerPopover'
import { PronounsView } from '../../Patient/PronounsView'
import { PatientThreadView } from '../../PatientThread'
import { AddPaymentPopover } from '../../Payments/AddPaymentPopover'
import { ProfileHeaderAlerts } from '../../ProfileHeaderAlerts'
import { ProfileSortColorPopover } from '../../SortColorPopover'
import { ProfileUrgentPopover } from '../../UrgentPopover'

const MidwifeRedFlagPopover = ({ redFlaggedReason }: { redFlaggedReason?: string }) => (
  <Popover trigger="hover">
    <PopoverTrigger>
      <WarningIcon
        ml={3}
        w={4}
        h={4}
        color={redFlaggedReason ? 'red.600' : 'gray.400'}
        cursor="pointer"
      />
    </PopoverTrigger>
    <PopoverContent w="auto">
      <PopoverArrow />
      {redFlaggedReason ? (
        <PopoverHeader fontWeight={600} color="red.600">
          RED FLAG
        </PopoverHeader>
      ) : null}
      <PopoverBody w="auto" minW="0" maxW="400px" p={0}>
        <Text
          whiteSpace="pre-wrap"
          p={2}
          fontSize="sm"
          color={redFlaggedReason ? 'red.600' : 'gray.600'}>
          {redFlaggedReason || 'Not red flagged'}
        </Text>
      </PopoverBody>
    </PopoverContent>
  </Popover>
)

const ActionPopovers = () => {
  const { appName } = useContext(AppContext)
  const {
    selectedAssessment: { id: assessmentId },
  } = useProfile()
  const { isMobile } = useContext(ScreenContext)
  return (
    <Flex w={isMobile ? '100%' : 'auto'} justify="center" gap={2} align="center">
      {appName === 'providers-app' ? <AddEncounterPopover /> : null}
      {appName === 'providers-app' && assessmentId ? <AddPaymentPopover /> : null}
      {appName === 'providers-app' && assessmentId ? (
        <UploadFilePopover accessLevel="practice" />
      ) : null}
      {assessmentId ? null : <GynInvitePopover />}
      {isMobile ? null : <ReportDeliveryPopover mobileLayout={isMobile} />}
    </Flex>
  )
}

export const PatientHeaderRow = ({ width }: { width: number }) => {
  const {
    selectedAssessment: { practiceRef, snippetRef, populated: selectedAssessment },
    user,
    patientRef,
    userId,
  } = useProfile()
  const { appName } = useContext(AppContext)
  const { nickname, fname, lname, pronouns } = user ?? {}
  const { midwife, redFlags, midwifeId } = selectedAssessment ?? {}
  const [showMidwife, setShowMidwife] = useState(false)
  const [nameModalOpen, setNameModalOpen] = useState(false)
  const update = useUpdateDoc()
  const midwifeName = useMemo(
    () => (midwife?.fname && midwife?.lname ? `${midwife.fname} ${midwife.lname}` : ''),
    [midwife],
  )

  const isMobile = useMemo(() => width < 1000, [width])
  const name = useMemo(
    () => `${fname || '[Missing first name]'} ${lname || '[Missing Last Name]'}`,
    [fname, lname],
  )

  return (
    <>
      <Stack
        borderBottom="1px solid #cdcdcd"
        divider={isMobile ? undefined : <StackDivider borderColor="blackAlpha.400" />}
        // align='center'
        flexFlow={isMobile ? 'column' : 'row'}
        bg="#FFC7BC"
        spacing={isMobile ? 0 : 4}
        py={2}
        minH="60px"
        align={isMobile ? 'flex-start' : 'center'}
        px={4}
        // pb={0}
      >
        <HStack spacing={3} py={0.5}>
          {selectedAssessment ? (
            <Flex>
              {appName === 'app' && snippetRef && selectedAssessment ? (
                <ProfileUrgentPopover />
              ) : null}
              {appName === 'providers-app' && practiceRef && selectedAssessment ? (
                <ProfileSortColorPopover />
              ) : null}
            </Flex>
          ) : null}
          <ProfilePicturePopover gutter={-28} userId={userId} canEdit size={10} noOnlineIndicator />
          <Tooltip placement="bottom" bg="gray.500" hasArrow fontSize="md" label="Edit name">
            <Button
              onClick={() => setNameModalOpen(true)}
              variant="link"
              fontSize="lg"
              fontFamily="Hero-New"
              py={2}
              px={0}
              fontWeight={500}
              color="gray.700">
              {name || 'No name entered'}
              {nickname ? ` (${nickname})` : ''}
            </Button>
          </Tooltip>
          {pronouns ? <PronounsView /> : null}
          <PartnerPopover />
          <PatientThreadView />
          {/* <ReturningPatientIcon /> */}
        </HStack>
        <Stack
          flexFlow={['column', 'column', 'column', 'row']}
          w={isMobile ? '100%' : 'auto'}
          pl={4}
          spacing={isMobile ? 1 : 3}>
          <AssessmentSelect />
          {isMobile ? <ActionPopovers /> : null}
          <Flex gap={2} align="center">
            {selectedAssessment && appName === 'app' ? (
              <Flex align="center">
                <Button
                  fontWeight={400}
                  color="blackAlpha.700"
                  bg="whiteAlpha.800"
                  _hover={{ bg: 'white' }}
                  style={{ alignItems: 'center' }}
                  onClick={() => setShowMidwife(true)}
                  alignItems="flex-start"
                  display="flex"
                  p={2}
                  variant="unstyled">
                  <VStack align="flex-start" spacing={0}>
                    <Text
                      fontFamily="Comfortaa"
                      lineHeight={1}
                      fontWeight={600}
                      isTruncated
                      fontSize="md"
                      maxW="250px">
                      {midwifeName}
                    </Text>
                    <Text lineHeight={1} isTruncated fontSize="sm" maxW="250px">
                      {midwife?.name || 'No practice name'}
                    </Text>
                  </VStack>
                  {appName === 'app' && midwife?.redFlaggedReason ? (
                    <MidwifeRedFlagPopover redFlaggedReason={midwife?.redFlaggedReason} />
                  ) : null}
                </Button>
              </Flex>
            ) : null}
            {appName === 'app' && selectedAssessment && redFlags ? (
              <Box>
                <ProfileRedFlagPopover redFlags={redFlags} />
              </Box>
            ) : null}
          </Flex>
        </Stack>
        {selectedAssessment ? (
          <HStack w={isMobile ? '100%' : 'auto'} pr={isMobile ? 0 : 10} ml={isMobile ? 0 : 'auto'}>
            {isMobile ? (
              <Flex gap={2} align="center" ml="auto">
                <ProfileHeaderAlerts />
                <ReportDeliveryPopover mobileLayout={isMobile} />
              </Flex>
            ) : (
              <ProfileHeaderAlerts />
            )}
          </HStack>
        ) : null}
        {isMobile ? null : <ActionPopovers />}
      </Stack>
      {showMidwife ? (
        <DefaultModal
          overlayHeader
          size={midwifeId ? '4xl' : 'xl'}
          contentProps={{ p: 0 }}
          isOpen={showMidwife}
          onClose={() => setShowMidwife(false)}
          render={() => <PatientMidwifeInfo />}
        />
      ) : null}
      <NameEditModal
        userType="patient"
        value={user}
        isOpen={nameModalOpen}
        onClose={() => setNameModalOpen(false)}
        onSubmit={v => update(patientRef, '', v)}
      />
    </>
  )
}
